import React from 'react';
import * as Styled from './styled';
import { BaseCarousel } from 'ds/components/carousels/base-carousel';
import BlogCard from 'components/blog-card';
import { useLocale } from 'locale';
import GlassesIcon from 'assets/svg/green-glasses.svg';
import { BlogCarouselProps, isEmpty, getDirection } from '../helper';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { noop } from 'lodash';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';

export interface SearchBlogCarouselProps extends BlogCarouselProps {
  isEmptySearch: boolean;
}

export const BlogSearchCarousel: React.FC<SearchBlogCarouselProps> = (props) => {
  const {
    articles,
    loading,
    isEmptySearch,
    onCarouselScroll = noop,
  } = props;

  const [ slideIndex, setSlideIndex ] = React.useState(0);
  const carouselSectionNode = React.useRef<HTMLDivElement>(null);
  const { t } = useLocale();
  const mediaBreakpoint = useScreenBreakpoint();
  const isMobile = mediaBreakpoint <= 2;
  const onCarouselSlideChange = (idx: number) => {
    onCarouselScroll(getDirection(slideIndex, idx), isMobile);
    setSlideIndex(idx);
  };

  const [ sectionInView, setSectionInView ] = React.useState(false);
  useIntersectionObserver(([ entry ]) => {
    setSectionInView(entry.isIntersecting);
  }, [ carouselSectionNode ], { threshold: 1 });

  if (isEmpty(articles, loading)) return null;

  const title = t(isEmptySearch ? 'searchListView.emptySearch.blog.title' : 'blog.search.carousel.title');
  const subTitle = t(isEmptySearch ? 'searchListView.emptySearch.blog.subtitle' : 'blog.search.carousel.subtitle');

  return (
    <Styled.Root ref={carouselSectionNode} data-auto="articles-carousel">
      <Styled.Header className="blog-header">
        <GlassesIcon width={42} height={42} />
        <Styled.InfoBox>
          <Styled.Title data-auto="articles-carousel-title" weight="bold">
            {title}
          </Styled.Title>
          <Styled.SubTitle data-auto="articles-carousel-subtitle">
            {subTitle}
          </Styled.SubTitle>
        </Styled.InfoBox>
      </Styled.Header>
      <BaseCarousel
        hideShadow
        entryContentDXOverride={24}
        hideControls={isMobile}
        onSlideIndexChange={onCarouselSlideChange}
        activeCarouselSlide={slideIndex}
      >
        {articles.map((el, k, arr) => (
          <BlogCard
            key={k}
            articleId={el.id}
            sectionInView={sectionInView}
            rootNode={carouselSectionNode}
            title={el.title}
            imageUrl={el.image}
            developerId={el.owner.id}
            developerName={el.owner.name}
            postUrl={el.link}
            withLeftMargin={k !== arr.length - 1}
          />
        ))}
      </BaseCarousel>
    </Styled.Root>
  );
};
